/**
 * Variant URL and Image Handler
 * Handles variant URL updates and image switching when variants are selected
 * Replicates Horizon's behavior for the main-product section
 */

export default class VariantUrlImageHandler {
  constructor() {
    this.init();
  }

  init() {
    // Initialize on page load
    this.setupVariantHandlers();
    
    // Listen for variant selection events
    document.addEventListener('variant:selected', (event) => {
      this.handleVariantSelection(event);
    });

    // Listen for variant update events
    document.addEventListener('variant:updated', (event) => {
      this.handleVariantUpdate(event);
    });

    // Initialize on DOM content loaded
    document.addEventListener('DOMContentLoaded', () => {
      this.setupVariantHandlers();
    });

    document.addEventListener('variant:loaded', () => {
      this.setupVariantHandlers();
    });
  }

  /**
   * Set up variant handlers for all variant pickers on the page
   */
  setupVariantHandlers() {
    const variantPickers = document.querySelectorAll('.variant-picker');
    
    variantPickers.forEach(picker => {
      this.initializeVariantPicker(picker);
    });
  }

  /**
   * Initialize a specific variant picker
   * @param {HTMLElement} picker - The variant picker element
   */
  initializeVariantPicker(picker) {
    // Check if this is on a product page
    const isProductPage = picker.dataset.templateProductMatch === 'true';
    if (!isProductPage) return;

    // Find all variant option inputs
    const variantInputs = picker.querySelectorAll('input[type="radio"][data-variant-id]');
    
    variantInputs.forEach(input => {
      // Remove existing listeners to prevent duplicates
      input.removeEventListener('change', this.handleVariantInputChange);
      
      // Add change event listener
      input.addEventListener('change', (event) => {
        // Instantly update selected label state
        this.updateSelectedLabelClass(picker, event.target);
        this.handleVariantInputChange(event, picker);
      });
    });
  }

  /**
   * Handle variant input change
   * @param {Event} event - The change event
   * @param {HTMLElement} picker - The variant picker element
   */
  handleVariantInputChange(event, picker) {
    const input = event.target;
    const variantId = input.dataset.variantId;
    
    if (!variantId) return;

    // Update URL with variant parameter
    this.updateUrl(variantId);
    
    // Switch to variant image
    this.switchToVariantImage(variantId, input);
    
    // Dispatch custom events for other components
    this.dispatchVariantEvents(variantId, input);
  }

  /**
   * Update selected_variant class on labels within the same option group
   * @param {HTMLElement} picker - The variant picker element
   * @param {HTMLInputElement} changedInput - The input that changed
   */
  updateSelectedLabelClass(picker, changedInput) {
    if (!picker || !changedInput) return;

    const inputName = changedInput.getAttribute('name');
    if (!inputName) return;

    // Find all radios in the same group within this picker
    const groupInputs = Array.from(picker.querySelectorAll('input[type="radio"]'))
      .filter((el) => el.getAttribute('name') === inputName);

    // Remove class from all labels in the group
    groupInputs.forEach((el) => {
      const lbl = el.closest('label');
      if (lbl) lbl.classList.remove('selected_variant', 'selected');
    });

    // Add class to the currently checked input's label
    const activeLabel = changedInput.closest('label');
    if (activeLabel) activeLabel.classList.add('selected_variant');
  }

  /**
   * Update the browser URL with the variant parameter
   * @param {string} variantId - The variant ID
   */
  updateUrl(variantId) {
    const url = new URL(window.location.href);
    
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    
    // Update URL without page reload
    if (url.href !== window.location.href) {
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Switch to the variant's featured image in the media gallery
   * @param {string} variantId - The variant ID
   * @param {HTMLElement} input - The variant input element
   */
  switchToVariantImage(variantId, input) {
    // Check if carousel is enabled by looking for the carousel wrapper
    const hasCarouselEnabled = this.isCarouselEnabled();
    
    if (hasCarouselEnabled) {
      // Use Swiper API to navigate to the slide
      this.navigateToVariantSlide(variantId);
    } else {
      // Use DOM reordering for static gallery
      this.reorderMediaGalleryImages(variantId);
    }
    
    // Only scroll to top if this is a color swatch variant
    if (this.isColorSwatchVariant(input)) {
      this.scrollToTop();
    }
  }

  /**
   * Check if carousel is enabled by looking for carousel wrapper in DOM
   * @returns {boolean} - True if carousel wrapper exists
   */
  isCarouselEnabled() {
    return document.querySelector('.pdp-carousel-wrapper') !== null;
  }

  /**
   * Navigate Swiper carousel to variant image slide
   * Uses Swiper's slideTo API instead of DOM manipulation
   * @param {string} variantId - The variant ID
   */
  getMainSwiperInstance() {
    const swiperElement = document.querySelector('.pdp-carousel__main .swiper');
    if (!swiperElement) return null;
    return swiperElement.swiper || null;
  }

  getThumbsSwiperInstance() {
    const swiperElement = document.querySelector('.pdp-carousel__thumbs .swiper');
    if (!swiperElement) return null;
    return swiperElement.swiper || null;
  }


  navigateToVariantSlide(variantId) {
    const pdpSwiper = this.getMainSwiperInstance();
    if (!pdpSwiper || !pdpSwiper.slides) {
      // Wait for Swiper to initialize
      const checkSwiper = setInterval(() => {
        const swiperInstance = this.getMainSwiperInstance();
        if (swiperInstance && swiperInstance.slides) {
          clearInterval(checkSwiper);
          this.navigateToVariantSlide(variantId);
        }
      }, 100);
      
      // Safety timeout after 3 seconds
      setTimeout(() => clearInterval(checkSwiper), 3000);
      return;
    }

    const slides = pdpSwiper.slides;
    let variantSlideIndex = -1;

    // Find the slide associated with this variant
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      // Query the media item within the slide
      const mediaItem = slide.querySelector('.pdp__media-item[data-variants-ids]');
      if (mediaItem) {
        const variantIds = mediaItem.dataset.variantsIds;
        if (variantIds && variantIds.includes(variantId)) {
          variantSlideIndex = i;
          break;
        }
      }
    }

    // If we found a variant slide, navigate to it
    if (variantSlideIndex >= 0) {
      // Use slideTo to navigate to the correct slide
      // First parameter is the slide index, second is speed in ms
      pdpSwiper.slideTo(variantSlideIndex, 300);
      
      // If we have thumbnails swiper, sync it
      const pdpThumbs = this.getThumbsSwiperInstance();
      if (pdpThumbs) {
        pdpThumbs.slideTo(variantSlideIndex, 300);
      }
    }
  }


  /**
   * Reorder media gallery images to bring variant image to front
   * @param {string} variantId - The variant ID
   */
  reorderMediaGalleryImages(variantId) {
    // Look for media container
    const mediaContainer = document.querySelector('.pdp__media');
    if (!mediaContainer) return;

    const mediaItems = Array.from(mediaContainer.querySelectorAll('.pdp__media-item'));
    let variantMediaItem = null;
    let variantItemIndex = -1;

    // Find the media item associated with this variant
    mediaItems.forEach((item, index) => {
      const variantIds = item.dataset.variantsIds;
      if (variantIds && variantIds.includes(variantId)) {
        variantMediaItem = item;
        variantItemIndex = index;
      }
    });

    // If we found a variant media item and it's not already first
    if (variantMediaItem && variantItemIndex > 0) {
      // Remove the item from its current position
      variantMediaItem.remove();
      
      // Insert it at the beginning of the media container
      mediaContainer.insertBefore(variantMediaItem, mediaContainer.firstChild);
      
      // Reset horizontal scroll position to show the newly positioned image
      this.resetMediaContainerScroll(mediaContainer);
      
      // Update active states
      this.updateActiveStates(variantMediaItem);
    } else if (variantMediaItem) {
      // Item is already first, just update active states
      this.updateActiveStates(variantMediaItem);
      
      // Still reset scroll position in case user had scrolled
      this.resetMediaContainerScroll(mediaContainer);
    }
  }

  /**
   * Update active states for media items
   * @param {HTMLElement} activeItem - The item to make active
   */
  updateActiveStates(activeItem) {
    // Remove active class from all media items
    const allMediaItems = document.querySelectorAll('.pdp__media-item');
    allMediaItems.forEach(mediaItem => {
      mediaItem.classList.remove('active', 'current');
    });
    
    // Add active class to selected item
    if (activeItem) {
      activeItem.classList.add('active', 'current');
    }
  }

  /**
   * Reset horizontal scroll position of media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  resetMediaContainerScroll(mediaContainer) {
    if (!mediaContainer) return;
    
    // Check if we're on a screen size where horizontal scrolling is active
    // The pdp__media has whitespace-nowrap on mobile/tablet, whitespace-normal on lg+
    const isLargeScreen = window.matchMedia('(min-width: 1024px)').matches;
    
    // Only reset scroll on smaller screens where horizontal scrolling is active
    if (!isLargeScreen) {
      // Immediately jump to the beginning (leftmost position)
      mediaContainer.scrollLeft = 0;
    }
  }

  /**
   * Check if the variant input is a color swatch
   * @param {HTMLElement} input - The variant input element
   * @returns {boolean} - True if this is a color swatch variant
   */
  isColorSwatchVariant(input) {
    if (!input) return false;
    
    // Check if the input is within a swatch variant option
    const label = input.closest('.variant-option__button-label--has-swatch');
    if (label) return true;
    
    // Check if the parent fieldset has swatch classes
    const fieldset = input.closest('.variant-option--swatches');
    if (fieldset) return true;
    
    // Check if this is a color swatch based on name attribute (fallback)
    const name = input.getAttribute('name');
    if (name && (name.toLowerCase().includes('color') || name.toLowerCase().includes('colour'))) {
      return true;
    }
    
    return false;
  }

  /**
   * Scroll page to top smoothly
   */
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Handle variant selection event
   * @param {CustomEvent} event - The variant selection event
   */
  handleVariantSelection(event) {
    const { variantId, variant, element } = event.detail;
    
    if (variantId) {
      this.updateUrl(variantId);
      
      // Check if carousel is enabled
      const hasCarouselEnabled = this.isCarouselEnabled();
      
      if (hasCarouselEnabled) {
        // Use Swiper API to navigate to the slide
        this.navigateToVariantSlide(variantId);
      } else {
        // Use DOM reordering for static gallery
        this.reorderMediaGalleryImages(variantId);
      }
      
      // Only scroll to top if this is a color swatch variant
      if (this.isColorSwatchVariant(element)) {
        this.scrollToTop();
      }
    }
  }

  /**
   * Handle variant update event
   * @param {CustomEvent} event - The variant update event
   */
  handleVariantUpdate(event) {
    const { variantId, variant, element } = event.detail;
    
    if (variantId) {
      // Check if carousel is enabled
      const hasCarouselEnabled = this.isCarouselEnabled();
      
      if (hasCarouselEnabled) {
        // Use Swiper API to navigate to the slide
        this.navigateToVariantSlide(variantId);
      } else {
        // Use DOM reordering for static gallery
        this.reorderMediaGalleryImages(variantId);
      }
      
      // Only scroll to top if this is a color swatch variant
      if (this.isColorSwatchVariant(element)) {
        this.scrollToTop();
      }
    }
  }

  /**
   * Dispatch custom variant events for other components
   * @param {string} variantId - The variant ID
   * @param {HTMLElement} input - The variant input element
   */
  dispatchVariantEvents(variantId, input) {
    // Dispatch variant selected event
    const variantSelectedEvent = new CustomEvent('variant:selected', {
      detail: {
        variantId: variantId,
        element: input,
        timestamp: Date.now()
      },
      bubbles: true
    });
    
    document.dispatchEvent(variantSelectedEvent);
    
    // Dispatch variant changed event for compatibility
    const variantChangedEvent = new CustomEvent('variant:changed', {
      detail: {
        variantId: variantId,
        element: input,
        timestamp: Date.now()
      },
      bubbles: true
    });
    
    document.dispatchEvent(variantChangedEvent);
  }

  /**
   * Get variant data from product JSON
   * @param {string} variantId - The variant ID
   * @returns {Object|null} - The variant data or null
   */
  getVariantData(variantId) {
    // Try to find product JSON data
    const productJson = document.querySelector('script[type="application/json"][data-product-json]');
    if (!productJson) return null;
    
    try {
      const productData = JSON.parse(productJson.textContent);
      return productData.variants?.find(variant => variant.id.toString() === variantId.toString()) || null;
    } catch (error) {
      console.warn('Failed to parse product JSON:', error);
      return null;
    }
  }

  /**
   * Initialize from URL parameter on page load
   */
  initializeFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      // Find and select the corresponding variant input
      const variantInput = document.querySelector(`input[data-variant-id="${variantId}"]`);
      if (variantInput && !variantInput.checked) {
        variantInput.checked = true;
        // Update selected class immediately for the correct picker
        const picker = variantInput.closest('.variant-picker');
        if (picker) this.updateSelectedLabelClass(picker, variantInput);
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Navigate to variant image
      const hasCarouselEnabled = this.isCarouselEnabled();
      
      if (hasCarouselEnabled) {
        // Use Swiper API to navigate to the slide (wait for Swiper to be ready)
        this.navigateToVariantSlide(variantId);
      } else {
        // Use DOM reordering for static gallery
        this.reorderMediaGalleryImages(variantId);
      }
    }
  }

  /**
   * Refresh the handler (useful for dynamic content updates)
   */
  refresh() {
    this.setupVariantHandlers();
    this.initializeFromUrl();
  }
}

// Auto-initialize when the script loads
const variantUrlImageHandler = new VariantUrlImageHandler();

// Initialize from URL on page load
document.addEventListener('DOMContentLoaded', () => {
  variantUrlImageHandler.initializeFromUrl();
});

// Export for manual initialization if needed
export { VariantUrlImageHandler };
